import { Component, OnInit } from '@angular/core';
import { GungNotificationService, OptionsListMulti } from 'gung-common';
import { ColmecWorkOrder } from '../colmec-working-order-wrapper/colmec-working-order-wrapper.component';
import { ColmecPartnerAgreementsService } from '../../../services/colmec-partner-agreements.service';
import {
  AuthService,
  CustomerService,
  MetadataService,
  SelectedCustomerService,
  gungAddRemoveSpinner
} from 'gung-standard';
import { TranslateService } from '@ngx-translate/core';
import { catchError, first, forkJoin, of, switchMap } from 'rxjs';
import { format } from 'date-fns';
import { ActivatedRoute } from '@angular/router';
import { ColmecWorkingOrderService } from '../../../services/colmec-working-order.service';

@Component({
    selector: 'colmec-working-order-details',
    templateUrl: './colmec-working-order-details.component.html',
    styleUrls: ['./colmec-working-order-details.component.scss'],
    standalone: false
})
export class ColmecWorkingOrderDetailsComponent implements OnInit {
  public calculatedTotals: { total: number; currency: string };
  public calculatedTotalsTires: { qty: number; total: number; currency: string };
  public calculatedTotalsServices: { qty: number; total: number; currency: string };
  public calculatedTotalsExtras: { qty: number; total: number; currency: string };

  public reasonForWorkOptionsList: OptionsListMulti[] = [];

  public workingOrder: ColmecWorkOrder;

  public basicInfo: {
    agreementName: string;
    date: string;
    regNr: string;
    reasons: string;
    workState: string;
    comment: string;
  };

  public isPartner: boolean = false;
  public isAdmin: boolean = false;
  public isSales: boolean = false;
  public actionsDisabled: boolean = false;
  public error: any;

  constructor(
    protected agreementService: ColmecPartnerAgreementsService,
    protected metadataService: MetadataService,
    protected customerService: CustomerService,
    protected translateService: TranslateService,
    protected route: ActivatedRoute,
    protected colmecWorkingOrderService: ColmecWorkingOrderService,
    protected authService: AuthService,
    protected selectedCustomerService: SelectedCustomerService,
    protected gungNotificationService: GungNotificationService
  ) { }

  ngOnInit(): void {
    this.route.params
      .pipe(
        first(),
        switchMap(params =>
          forkJoin({
            data: this.colmecWorkingOrderService.getWorkingOrder(params?.id || '', true).pipe(first()),
            isAdmin: this.authService.getHasRoles('ADMIN').pipe(first()),
            isSales: this.authService.getHasRoles('SALES').pipe(first()),
            selectedCustomer: this.selectedCustomerService.getSelectedCustomer().pipe(first())
          })
        )
      )
      .subscribe(({ data, isAdmin, isSales, selectedCustomer }) => {
        this.isAdmin = isAdmin;
        this.isSales = isSales;

        if (!!selectedCustomer.extra?.kus?.q_partner_boolean) {
          this.isPartner = true;
        } else {
          this.isPartner = false;
        }

        if (!!data) {
          this.workingOrder = data;
          this.init();
        }
      });
  }

  protected init(): void {
    this.calculateAllTotals();
    this.getReasonForWorkData();

    if (
      this.workingOrder.workState === 'APPROVED' ||
      this.workingOrder.workState === 'DECLINED' ||
      this.workingOrder.workState === 'CANCELLED'
    ) {
      this.actionsDisabled = true;
    } else {
      this.actionsDisabled = false;
    }

    this.agreementService
      .getPartnerAgreementById(this.workingOrder.agreementId)
      .pipe(first())
      .subscribe(pa => {
        let name: string = '';

        name += `${pa.customerName} (${pa.customerId}) - `;

        name += `${pa.partnerName} (${pa.partnerId})`;

        this.basicInfo = {
          agreementName: name,
          date: format(new Date(this.workingOrder.workPerformedDate), 'yyyy-MM-dd'),
          regNr: this.workingOrder.regNr,
          reasons: this.reasonForWorkOptionsList
            .filter(r => this.workingOrder.reasonForWork.includes(r.id))
            .map(r => r.name)
            .join(', '),
          workState: !!this.workingOrder?.workState ? this.translateService.instant(this.workingOrder.workState) : '',
          comment: this.workingOrder.extra.comment || ''
        };
      });
  }

  protected calculateAllTotals(): void {
    const tires: { qty: number; total: number; currency: string } = { qty: 0, total: 0, currency: '' };
    for (const row of this.workingOrder.tires) {
      tires.qty += row.qty;
      tires.total += row.price.value * row.qty;
      tires.currency = row.price.currency;
    }
    this.calculatedTotalsTires = tires;

    const services: { qty: number; total: number; currency: string } = { qty: 0, total: 0, currency: '' };
    for (const row of this.workingOrder.services) {
      services.qty += row.qty;
      services.total += row.price.value * row.qty;
      services.currency = row.price.currency;
    }
    this.calculatedTotalsServices = services;

    const extras: { qty: number; total: number; currency: string } = { qty: 0, total: 0, currency: '' };
    for (const row of this.workingOrder.extras) {
      extras.qty += row.extra?.qty || 1;
      extras.total += Number(row.price) * (row.extra?.qty || 1);
      extras.currency = tires.currency;
    }
    this.calculatedTotalsExtras = extras;

    this.calculatedTotals = {
      total: tires.total + services.total + extras.total,
      currency: tires.currency
    };
  }

  private getReasonForWorkData(): void {
    const workingOrderReasonsMetadata = this.metadataService.getMetadataTableList('WORKING_ORDER_REASONS');

    const reasons: OptionsListMulti[] = workingOrderReasonsMetadata.map(r => {
      return {
        id: r.id,
        name: this.translateService.instant(r.name || ''),
        selected: false
      };
    });

    for (const option of reasons) {
      if (this.workingOrder.reasonForWork.includes(option.id)) {
        option.selected = true;
      }
    }

    this.reasonForWorkOptionsList = reasons;
  }

  public changeWorkOrderStatus({ target }, status: string): void {
    gungAddRemoveSpinner(target);
    this.error = undefined;

    this.workingOrder.workState = status;
    this.colmecWorkingOrderService
      .updateWorkingOrder(this.workingOrder, this.isAdmin || this.isSales)
      .pipe(
        first(),
        catchError(_ => of(undefined))
      )
      .subscribe(result => {
        if (!!result && !result['error']) {
          this.workingOrder = result['data'];
          this.init();
          this.gungNotificationService.notify(
            this.translateService.instant('SUCCESS'),
            this.translateService.instant('WORK_ORDER_STATUS_CHANGED_SUCCESS'),
            'success'
          );
        } else if (!!result['error']) {
          this.error = result['error'];
          this.gungNotificationService.notify(
            this.translateService.instant('ERROR'),
            this.translateService.instant(result['error']),
            'error'
          );
        } else {
          this.error = 'WORK_ORDER_GENERIC_ERROR';
          this.gungNotificationService.notify(
            this.translateService.instant('ERROR'),
            this.translateService.instant('WORK_ORDER_GENERIC_ERROR'),
            'error'
          );
        }
        gungAddRemoveSpinner(target);
      });
  }
}
